import type { MouseEvent } from 'react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { XIcon } from '@phosphor-icons/react';
import { AlertRuleEvent } from '@/types/event';
import { EventFeedCard } from '@/features/EventFeed/components';
import { useOpenInNewWindow } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import {
    alertKey,
    isAlertNew,
    useAlertsStore,
    useSourcesStore,
} from '@/stores';
import { getAlertDescription } from '@/utils/detection-classes';
import { getEventTimeRange } from '@/utils/event-time-range';
import {
    CARD_INITIAL,
    CARD_ANIMATE,
    CARD_EXIT,
    CARD_EXIT_REDUCED,
    getCardTransition,
} from './animations';

interface AlertNotificationCardProps {
    notification: AlertRuleEvent;
    onClose: (notificationKey: string) => void;
    shouldReduceMotion?: boolean | null;
}

export const AlertNotificationCard = memo(function AlertNotificationCard({
    notification,
    onClose,
    shouldReduceMotion = false,
}: AlertNotificationCardProps) {
    const { t: tCommon } = useTranslation(I18nNamespace.COMMON);
    const { t: tEvents } = useTranslation(I18nNamespace.EVENTS);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const alertArrivals = useAlertsStore((state) => state.alertArrivals);
    const { openEvent } = useOpenInNewWindow();
    const event = notification.event;
    const source = getSourceById(event.sourceId);
    const sourceType = event.sourceType ?? source?.type;
    const isScene = event.type === 'SCENE' || event.classId < 0;
    const { startTime: eventStartTime, endTime: eventEndTime } =
        getEventTimeRange(event);
    const notificationKey = alertKey(notification);
    const isNew = isAlertNew(alertArrivals, notificationKey);

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        openEvent(notification.event.id, event, {
            state: {
                from: 'alerts',
                feedContext: {
                    type: 'alertRule',
                    alertRuleId: notification.alertRuleId,
                },
            },
        });
    };

    const handleClose = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onClose(notificationKey);
        },
        [notificationKey, onClose],
    );

    return (
        <motion.div
            layout={!shouldReduceMotion}
            initial={shouldReduceMotion ? false : CARD_INITIAL}
            animate={CARD_ANIMATE}
            exit={shouldReduceMotion ? CARD_EXIT_REDUCED : CARD_EXIT}
            transition={getCardTransition(shouldReduceMotion)}
            className="relative mb-2"
        >
            <button
                type="button"
                onClick={handleClose}
                className="absolute right-1 top-1 z-10 p-1 text-grey-300 hover:text-white-100 active:scale-95 transition-colors"
                aria-label={tCommon('button.closeNotification')}
            >
                <XIcon size={12} weight="bold" />
            </button>

            <EventFeedCard
                thumbnailUrl={
                    event.thumbnailUrl ||
                    (isScene
                        ? EngineService.getSceneThumbnailUrl(event.id)
                        : EngineService.getEventThumbnailUrl(event.id, true))
                }
                isScene={isScene}
                title={tEvents('detection.alert')}
                description={
                    isScene
                        ? notification.promptText
                        : getAlertDescription(
                              tEvents,
                              notification.promptText,
                              event.classId,
                          )
                }
                timestamp={event.createdAt}
                timeRange={
                    eventStartTime !== undefined && eventEndTime !== undefined
                        ? {
                              start: eventStartTime,
                              end: eventEndTime,
                          }
                        : undefined
                }
                cameraName={
                    notification.sourceName ??
                    event.sourceName ??
                    source?.displayName ??
                    undefined
                }
                sourceType={sourceType}
                confidence={notification.confidence}
                useRelativeTime
                isNew={isNew}
                onClick={handleClick}
            />
        </motion.div>
    );
});
